import { useCallback } from 'react'
import { TaskActualWorkApproveUserDto } from '../../../../../applications/ports/taskActualWorkApproveRepository'
import { intl } from '../../../../../i18n'
import {
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Slide,
  Badge,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import { FilterInfo, useFilterState } from './hooks/useFilterState'

interface FilterPanelProps {
  open: boolean
  onClose: () => void
  allUsersWithStatus: TaskActualWorkApproveUserDto[]
  onUserSelect: (user: TaskActualWorkApproveUserDto) => void
  onReload: () => void
  onFilterChange?: (filterInfo: FilterInfo) => void
  onFilteredUsersChange?: (users: TaskActualWorkApproveUserDto[]) => void
}

const FilterPanel = ({
  open,
  onClose,
  allUsersWithStatus,
  onUserSelect,
  onReload,
  onFilterChange,
  onFilteredUsersChange,
}: FilterPanelProps) => {
  const {
    tabIndex,
    selectedDivisionUuid,
    selectedTeamUuid,
    setTabIndex,
    setSelectedDivisionUuid,
    setSelectedTeamUuid,
    divisions,
    teams,
    filteredUsers,
    requestingUsersCount,
  } = useFilterState({
    allUsersWithStatus,
    onFilterChange,
    onFilteredUsersChange,
  })

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setTabIndex(newValue)
    },
    []
  )

  const handleDivisionChange = useCallback((event: any) => {
    const value = event.target.value
    setSelectedDivisionUuid(value || undefined)
  }, [])

  const handleTeamChange = useCallback((event: any) => {
    const value = event.target.value
    setSelectedTeamUuid(value || undefined)
  }, [])

  const handleUserClick = useCallback(
    (user: TaskActualWorkApproveUserDto) => {
      onUserSelect(user)
    },
    [onUserSelect]
  )

  return (
    <StyledSlide in={open} direction="right">
      <DialogContainer>
        <DialogHeader>
          <DialogTitle>
            {intl.formatMessage({ id: 'taskActualWorkApprove.filter.title' })}
          </DialogTitle>
          <HeaderButtons>
            <RefreshIconButton onClick={onReload} size="small">
              <RefreshIcon />
            </RefreshIconButton>
            <CloseIconButton onClick={onClose} size="small">
              <CloseIcon />
            </CloseIconButton>
          </HeaderButtons>
        </DialogHeader>
        <StyledDivider />

        <FilterControls>
          <FormControl fullWidth={true} size="small">
            <InputLabel>
              {intl.formatMessage({
                id: 'taskActualWorkApprove.filter.division',
              })}
            </InputLabel>
            <Select
              value={selectedDivisionUuid || ''}
              onChange={handleDivisionChange}
              label={intl.formatMessage({
                id: 'taskActualWorkApprove.filter.division',
              })}
            >
              <MenuItem value="">
                {intl.formatMessage({
                  id: 'taskActualWorkApprove.filter.all',
                })}
              </MenuItem>
              {divisions.map(div => (
                <MenuItem key={div.uuid} value={div.uuid}>
                  {div.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth={true} size="small">
            <InputLabel>
              {intl.formatMessage({ id: 'taskActualWorkApprove.filter.team' })}
            </InputLabel>
            <Select
              value={selectedTeamUuid || ''}
              onChange={handleTeamChange}
              label={intl.formatMessage({
                id: 'taskActualWorkApprove.filter.team',
              })}
            >
              <MenuItem value="">
                {intl.formatMessage({
                  id: 'taskActualWorkApprove.filter.all',
                })}
              </MenuItem>
              {teams.map(team => (
                <MenuItem key={team.uuid} value={team.uuid}>
                  {team.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FilterControls>

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: `1px solid ${colorPalette.monotone[2]}` }}
        >
          <Tab
            label={
              <Badge
                badgeContent={requestingUsersCount}
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: colorPalette.pink[2],
                    color: colorPalette.pink[7],
                    fontSize: '10px',
                    height: '18px',
                    minWidth: '18px',
                    padding: '0 4px',
                  },
                }}
              >
                <Box sx={{ px: 1 }}>
                  {intl.formatMessage({
                    id: 'taskActualWorkApprove.filter.requesting',
                  })}
                </Box>
              </Badge>
            }
          />
          <Tab
            label={intl.formatMessage({
              id: 'taskActualWorkApprove.filter.unrequested',
            })}
          />
          <Tab
            label={intl.formatMessage({
              id: 'taskActualWorkApprove.filter.tab.all',
            })}
          />
        </Tabs>

        <DialogContent>
          {filteredUsers.length === 0 ? (
            <EmptyMessage>
              {intl.formatMessage({
                id: 'taskActualWorkApprove.filter.userList.empty',
              })}
            </EmptyMessage>
          ) : (
            <>
              {filteredUsers.map(user => (
                <UserRow
                  key={user.projectMemberDetail.uuid}
                  onClick={() => handleUserClick(user)}
                >
                  <UserInfoContainer>
                    <UserTextInfo>
                      <UserName>{user.projectMemberDetail.user.name}</UserName>
                      <UserDivision>
                        {intl.formatMessage({
                          id: 'taskActualWorkApprove.filter.division',
                        })}
                        :{' '}
                        {user.projectMemberDetail.user.division?.displayName ||
                          ''}
                      </UserDivision>
                      <UserTeam>
                        {intl.formatMessage({
                          id: 'taskActualWorkApprove.filter.team',
                        })}
                        : {user.projectMemberDetail.team?.displayName || ''}
                      </UserTeam>
                    </UserTextInfo>
                    <ChipContainer>
                      <Chip
                        label={`${intl.formatMessage({ id: 'taskActualWorkApprove.filter.requesting' })}: ${user.requestingCount}`}
                        size="small"
                        sx={{
                          backgroundColor:
                            user.requestingCount > 0
                              ? colorPalette.pink[2]
                              : colorPalette.monotone[2],
                          color:
                            user.requestingCount > 0
                              ? colorPalette.pink[7]
                              : colorPalette.monotone[7],
                        }}
                      />
                      <Chip
                        label={`${intl.formatMessage({ id: 'taskActualWorkApprove.filter.unrequested' })}: ${user.unrequestedCount}`}
                        size="small"
                        sx={{
                          backgroundColor:
                            user.unrequestedCount > 0
                              ? colorPalette.yellow[2]
                              : colorPalette.monotone[2],
                          color:
                            user.unrequestedCount > 0
                              ? colorPalette.yellow[7]
                              : colorPalette.monotone[7],
                        }}
                      />
                    </ChipContainer>
                  </UserInfoContainer>
                </UserRow>
              ))}
            </>
          )}
        </DialogContent>
      </DialogContainer>
    </StyledSlide>
  )
}

const StyledSlide = styled(Slide)({
  position: 'absolute',
  top: '93px',
  left: 41,
  zIndex: 10,
  height: 'calc(100% - 93px)',
})

const DialogContainer = styled('div')({
  width: '400px',
  height: '100%',
  backgroundColor: colorPalette.monotone[0],
  border: `1px solid ${colorPalette.monotone[2]}`,
  boxShadow: `0px 4px 16px 0px ${colorPalette.monotone[4]}80`,
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
})

const DialogHeader = styled(Box)({
  padding: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const DialogTitle = styled('span')({
  fontSize: '14px',
  color: colorPalette.monotone[10],
  fontWeight: 600,
})

const HeaderButtons = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const CloseIconButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: '4px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const CloseIcon = styled(CloseRoundedIcon)({
  fontSize: '16px',
})

const RefreshIconButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: '4px',
  color: colorPalette.monotone[4],
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const RefreshIcon = styled(RefreshRoundedIcon)({
  fontSize: '16px',
})

const StyledDivider = styled(Divider)({
  margin: '0 16px',
  borderColor: colorPalette.monotone[2],
})

const FilterControls = styled(Box)({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
})

const DialogContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
})

const EmptyMessage = styled(Box)({
  padding: '32px 16px',
  textAlign: 'center',
  color: colorPalette.monotone[7],
  fontSize: '14px',
})

const UserRow = styled(Box)({
  display: 'flex',
  cursor: 'pointer',
  padding: '12px 16px',
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

const UserInfoContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  gap: '8px',
})

const UserTextInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
})

const UserName = styled('div')({
  fontSize: '14px',
  fontWeight: 500,
  color: colorPalette.monotone[10],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const UserDivision = styled('div')({
  fontSize: '12px',
  color: colorPalette.monotone[7],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const UserTeam = styled('div')({
  fontSize: '12px',
  color: colorPalette.monotone[7],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ChipContainer = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignSelf: 'center',
  flexShrink: 0,
})

export default FilterPanel
